import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import PsychologyIcon from "@mui/icons-material/Psychology";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import HistoryIcon from "@mui/icons-material/History";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useLocation, useNavigate } from "react-router-dom";
import {
  executeCasperEntity,
  getCasperProfile,
  getCasperSession,
  runCasperChat,
  startOAuthConnect,
  streamAgentRunResult,
  submitCasperFeedback,
} from "../../api/agentApi";
import { useUser } from "../UserContext";
import CasperMarkdown from "./CasperMarkdown.jsx";

const copper = "#B87333";
const copperDark = "#935C29";
const CASPER_OVERLAY_Z_INDEX = 2400;
const commandIconSx = {
  width: 42,
  height: 42,
  border: "1px solid rgba(184,115,51,0.28)",
  color: copperDark,
  bgcolor: "rgba(246,240,235,0.72)",
  boxShadow: "0 10px 24px rgba(20,18,15,0.06)",
  transition: "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
  "&:hover": {
    bgcolor: "rgba(184,115,51,0.12)",
    transform: "translateY(-1px)",
    boxShadow: "0 14px 30px rgba(20,18,15,0.10)",
  },
  "&.Mui-disabled": {
    opacity: 0.45,
    color: "text.disabled",
  },
};

const messageActionButtonSx = (active = false) => ({
  width: 30,
  height: 30,
  borderRadius: "50%",
  border: `1px solid ${active ? copper : "rgba(184,115,51,0.24)"}`,
  color: active ? copperDark : "#8a98aa",
  bgcolor: active ? "rgba(184,115,51,0.11)" : "rgba(255,255,255,0.78)",
  boxShadow: active ? "0 7px 18px rgba(184,115,51,0.18)" : "none",
  transition: "all 0.16s ease",
  "& .MuiSvgIcon-root": {
    fontSize: 16,
  },
  "&:hover": {
    borderColor: copper,
    color: copperDark,
    bgcolor: "rgba(184,115,51,0.12)",
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    borderColor: "rgba(138,152,170,0.14)",
    color: "rgba(138,152,170,0.34)",
    bgcolor: "rgba(255,255,255,0.45)",
    transform: "none",
  },
});

function localMessage(role, content, extra = {}) {
  return {
    id: extra.id || `bubble-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    role,
    content,
    status: extra.status || "completed",
    kind: extra.kind || "message",
    metadata: extra.metadata,
  };
}

function MiniGhost() {
  return (
    <Box
      sx={{
        width: 32,
        height: 32,
        position: "relative",
        borderRadius: "48% 48% 42% 42%",
        background: "linear-gradient(145deg, #D1A17A 0%, #B87333 58%, #8E5727 100%)",
        "&:before": {
          content: '""',
          position: "absolute",
          width: 4,
          height: 4,
          borderRadius: "50%",
          background: "#10100F",
          top: 12,
          left: 10,
          boxShadow: "9px 0 0 #10100F",
        },
        "&:after": {
          content: '""',
          position: "absolute",
          right: -9,
          bottom: 3,
          width: 18,
          height: 8,
          borderRadius: "999px",
          background: "linear-gradient(90deg, rgba(184,115,51,0.88), rgba(184,115,51,0))",
          transform: "rotate(-16deg)",
        },
      }}
    />
  );
}

export default function CasperBubble() {
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [intent, setIntent] = useState("");
  const [resolution, setResolution] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [authRequired, setAuthRequired] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const streamCleanupRef = useRef(null);

  useEffect(() => {
    if (!user || location.pathname === "/casper") return;
    let mounted = true;
    getCasperProfile()
      .then((data) => mounted && setProfile(data))
      .catch(() => mounted && setProfile(null));
    return () => {
      mounted = false;
    };
  }, [user, location.pathname]);

  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
        streamCleanupRef.current = null;
      }
    };
  }, []);

  const appendMessage = (message) => {
    setMessages((current) => [...current, message]);
  };

  const updateMessage = (messageId, patch) => {
    setMessages((current) => current.map((message) => (
      message.id === messageId
        ? { ...message, ...(typeof patch === "function" ? patch(message) : patch) }
        : message
    )));
  };

  const copyTextToClipboard = async (value) => {
    const text = String(value || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.setAttribute("readonly", "readonly");
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      textArea.remove();
    }
  };

  const shareCasperText = async ({ title = "Next Moca Casper", text = "" } = {}) => {
    const shareText = String(text || "").trim();
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: shareText || title,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }
    await copyTextToClipboard(`${shareText}${shareText ? "\n" : ""}${shareUrl}`);
  };

  const promptForAssistantMessage = (message) => {
    const index = messages.findIndex((item) => item.id === message.id);
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
      if (messages[cursor]?.role === "user") return messages[cursor].content || "";
    }
    return intent || "";
  };

  const submitOutcomeSignal = async (message, feedback, note = "") => {
    const messageId = message?.id;
    const messageKey = String(messageId || "");
    if (!activeSessionId || !messageId || messageKey.startsWith("bubble-") || message?.feedbackPending) {
      return;
    }
    const previousFeedback = message.feedback || null;
    const previousNote = message.feedbackNote || null;
    updateMessage(messageId, {
      feedback,
      feedbackNote: note || feedback,
      feedbackPending: true,
    });
    try {
      const response = await submitCasperFeedback({
        session_id: activeSessionId,
        message_id: messageId,
        feedback,
        prompt: promptForAssistantMessage(message),
        response: message.content || "",
        artifacts: artifactsForMessage(message),
        note,
      });
      const outcomeFeedback = response?.message?.metadata?.outcome_feedback || {};
      updateMessage(messageId, {
        feedback: outcomeFeedback.feedback || feedback,
        feedbackNote: outcomeFeedback.note || note || feedback,
        feedbackPending: false,
      });
    } catch (err) {
      updateMessage(messageId, {
        feedback: previousFeedback,
        feedbackNote: previousNote,
        feedbackPending: false,
      });
      setError(err.message || "Could not save outcome signal");
    }
  };

  const attachStream = (endpoint, sessionId, assistantMessageId) => {
    if (!endpoint || !assistantMessageId) return;
    if (streamCleanupRef.current) {
      streamCleanupRef.current();
      streamCleanupRef.current = null;
    }
    const stream = streamAgentRunResult(
      endpoint,
      (content) => updateMessage(assistantMessageId, { content, status: "streaming" }),
      async () => {
        updateMessage(assistantMessageId, {
          content: "Casper lost the live stream. Refreshing the final answer...",
          status: "failed",
        });
        try {
          const data = await getCasperSession(sessionId);
          const serverMessage = (data?.messages || []).find((message) => message.id === assistantMessageId);
          if (serverMessage) {
            updateMessage(assistantMessageId, {
              content: serverMessage.content || "",
              status: serverMessage.status || "completed",
              metadata: serverMessage.metadata,
              artifacts: serverMessage.artifacts,
              run_number: serverMessage.run_number,
              agent_version: serverMessage.agent_version,
            });
          }
        } catch {
          // Keep the visible stream warning.
        } finally {
          setBusy(false);
          streamCleanupRef.current = null;
        }
      },
      {
        typewriterSpeed: 0,
        onAnimationEnd: async () => {
          try {
            const data = await getCasperSession(sessionId);
            const serverMessage = (data?.messages || []).find((message) => message.id === assistantMessageId);
            if (serverMessage) {
              updateMessage(assistantMessageId, {
                content: serverMessage.content || "",
                status: serverMessage.status || "completed",
                metadata: serverMessage.metadata,
                artifacts: serverMessage.artifacts,
                run_number: serverMessage.run_number,
                agent_version: serverMessage.agent_version,
              });
            }
          } catch {
            // Streamed content is already visible.
          } finally {
            setBusy(false);
            streamCleanupRef.current = null;
          }
        },
      },
    );
    streamCleanupRef.current = stream.cleanup;
  };

  if (!user || location.pathname === "/casper") {
    return null;
  }

  const runAgent = async (agent = null) => {
    const value = intent.trim();
    if (!value) return;
    setBusy(true);
    setError("");
    setNotice(null);
    setResolution(null);
    appendMessage(localMessage("user", value));
    if (agent) {
      appendMessage(localMessage(
        "system",
        `I found ${agent.display_name || agent.name}. I’ll use this agent here.`,
        { kind: "status" },
      ));
    }
    try {
      const data = await runCasperChat({
        message: value,
        agent_id: agent?.id,
        agent_version: agent?.version,
        title: value.slice(0, 96),
      });
      if (data.session_id) setActiveSessionId(data.session_id);
      if (data.status === "needs_selection") {
        appendMessage(localMessage(
          "system",
          data.message || "Casper could not find a kernel-approved route for this request.",
          { kind: "status" },
        ));
        return;
      }
      if (data.status === "confirmation_required" || data.status === "blocked") {
        appendMessage(localMessage(
          "assistant",
          data.message || "Open the Casper workspace to review the executable preview before running this.",
          {
            status: data.status,
            metadata: {
              planner: data.planner,
              readiness: data.readiness,
              execution_preview: data.execution_preview,
            },
          },
        ));
        return;
      }
      if (data.status === "completed") {
        appendMessage(localMessage(
          "assistant",
          data.message || "Casper answered this from product context.",
          {
            status: "completed",
            metadata: {
              planner: data.planner,
              readiness: data.readiness,
            },
          },
        ));
        return;
      }
      const assistantMessage = data.assistant_message;
      if (assistantMessage?.id) {
        appendMessage({
          id: assistantMessage.id,
          role: "assistant",
          content: assistantMessage.content || "",
          status: assistantMessage.status || "pending",
          metadata: assistantMessage.metadata,
          artifacts: assistantMessage.artifacts,
          run_number: assistantMessage.run_number,
          agent_version: assistantMessage.agent_version,
        });
        if (data.casper_events_endpoint) {
          attachStream(data.casper_events_endpoint, data.session_id, assistantMessage.id);
          return;
        }
      } else {
        appendMessage(localMessage("assistant", data.message || "Casper started the work.", { status: "pending" }));
      }
    } catch (err) {
      setError(err.message || "Casper could not start that agent run");
    } finally {
      if (!streamCleanupRef.current) {
        setBusy(false);
      }
    }
  };

  const executeEntity = async (entity) => {
    if (!entity) return;
    if (entity.type === "agent") {
      await runAgent(entity);
      return;
    }
    setBusy(true);
    setError("");
    setNotice(null);
    setAuthRequired([]);
    appendMessage(localMessage(
      "system",
      `I found ${entity.display_name || entity.name}. I’ll use this ${entity.type} here.`,
      { kind: "status" },
    ));
    try {
      const data = await executeCasperEntity({
        intent: intent.trim() || entity.reason || entity.description || entity.name,
        entity_type: entity.type,
        entity_id: entity.id,
        entity_version: entity.version,
        action: entity.action,
        input: intent.trim() ? { intent: intent.trim() } : {},
      });
      if (data?.auth_required?.length) setAuthRequired(data.auth_required);
      setNotice(data);
      appendMessage(localMessage(
        "assistant",
        data?.message || `${entity.display_name || entity.name} is ready.`,
        { status: data?.status || "completed" },
      ));
    } catch (err) {
      setError(err.message || "Casper could not execute that action");
    } finally {
      setBusy(false);
    }
  };

  const connectOAuth = async (signal) => {
    if (!signal?.provider) return;
    setBusy(true);
    setError("");
    try {
      const response = await startOAuthConnect({
        provider: signal.provider,
        required_env: signal.required_env,
        tool_name: signal.tool_name,
        scopes: Array.isArray(signal.scopes) ? signal.scopes : undefined,
        return_to: window.location.href,
      });
      if (response?.authorization_url) {
        window.open(response.authorization_url, "nextmoca_casper_oauth", "popup,width=720,height=760");
      }
    } catch (err) {
      setError(err.message || "Could not start OAuth connection");
    } finally {
      setBusy(false);
    }
  };

  const suggestions = profile?.suggestions || [];
  const agents = resolution?.entities?.agents || [];
  const sideEntities = [
    ...(resolution?.entities?.workflows || []),
    ...(resolution?.entities?.tools || []),
    ...(resolution?.entities?.knowledge || []),
  ];
  const artifactsForMessage = (message) => {
    const metadataArtifacts = message?.metadata?.artifacts || message?.metadata?.generated_artifacts || [];
    const directArtifacts = message?.artifacts || [];
    return [...(Array.isArray(directArtifacts) ? directArtifacts : []), ...(Array.isArray(metadataArtifacts) ? metadataArtifacts : [])]
      .filter(Boolean)
      .map((artifact) => ({
        name: artifact.name || artifact.filename || artifact.path || "Artifact",
        filename: artifact.filename || artifact.name || artifact.path,
        type: artifact.type,
        url: artifact.url || artifact.download_url,
      }));
  };
  const artifactCount = messages.reduce((count, message) => count + artifactsForMessage(message).length, 0);

  const replayPrompt = async (value) => {
    const text = String(value || "").trim();
    if (!text) return;
    setIntent(text);
    setResolution(null);
  };

  const handlePrimarySubmit = async () => {
    if (!intent.trim() || busy) return;
    await runAgent(agents.length === 1 ? agents[0] : null);
  };

  const renderPromptActions = (message) => (
    <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="flex-end" sx={{ mt: 0.7, pr: 0.3 }}>
      <Tooltip title="Copy prompt">
        <span>
          <IconButton
            size="small"
            sx={messageActionButtonSx()}
            onClick={() => copyTextToClipboard(message.content)}
            disabled={!String(message.content || "").trim()}
          >
            <ContentCopyIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Share prompt">
        <span>
          <IconButton
            size="small"
            sx={messageActionButtonSx()}
            onClick={() => shareCasperText({ title: "Casper prompt", text: message.content })}
            disabled={!String(message.content || "").trim()}
          >
            <ShareIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Edit prompt">
        <span>
          <IconButton
            size="small"
            sx={messageActionButtonSx()}
            onClick={() => setIntent(message.content || "")}
            disabled={busy || !String(message.content || "").trim()}
          >
            <EditOutlinedIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Run again">
        <span>
          <IconButton
            size="small"
            sx={messageActionButtonSx()}
            onClick={() => replayPrompt(message.content)}
            disabled={busy || !String(message.content || "").trim()}
          >
            <PlayArrowIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );

  const renderResponseActions = (message) => {
    if (message.role !== "assistant" || message.kind !== "message" || message.status !== "completed") return null;
    const messageKey = String(message.id || "");
    const feedbackDisabled = !activeSessionId || messageKey.startsWith("bubble-") || message.feedbackPending;
    const positiveActive = message.feedback === "up";
    const negativeActive = message.feedback === "down";
    return (
      <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.7, pl: 0.3 }}>
        <Tooltip title="Share response">
          <span>
            <IconButton
              size="small"
              sx={messageActionButtonSx()}
              onClick={() => shareCasperText({ title: "Casper response", text: message.content })}
              disabled={!String(message.content || "").trim()}
            >
              <ShareIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Favorite result">
          <span>
            <IconButton
              size="small"
              sx={messageActionButtonSx(positiveActive)}
              onClick={() => submitOutcomeSignal(message, "up", "favorite_result")}
              disabled={feedbackDisabled}
            >
              {positiveActive ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Good outcome">
          <span>
            <IconButton
              size="small"
              sx={messageActionButtonSx(positiveActive)}
              onClick={() => submitOutcomeSignal(message, "up", "positive_outcome")}
              disabled={feedbackDisabled}
            >
              {positiveActive ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Poor outcome">
          <span>
            <IconButton
              size="small"
              sx={messageActionButtonSx(negativeActive)}
              onClick={() => submitOutcomeSignal(message, "down", "negative_outcome")}
              disabled={feedbackDisabled}
            >
              {negativeActive ? <ThumbDownIcon /> : <ThumbDownOutlinedIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.3 }}>
          Outcome signal
        </Typography>
      </Stack>
    );
  };

  const renderArtifacts = (message) => {
    const artifacts = artifactsForMessage(message);
    if (!artifacts.length) return null;
    return (
      <Stack spacing={0.8} sx={{ mt: 1.25 }}>
        {artifacts.slice(0, 3).map((artifact, index) => (
          <Box
            key={`${artifact.filename || artifact.name}-${index}`}
            sx={{
              p: 1,
              borderRadius: 1.5,
              border: "1px solid rgba(184,115,51,0.18)",
              bgcolor: "rgba(246,240,235,0.5)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Inventory2OutlinedIcon fontSize="small" sx={{ color: copperDark }} />
              <Typography variant="body2" fontWeight={800} noWrap sx={{ flex: 1 }}>
                {artifact.name || artifact.filename || "Artifact"}
              </Typography>
              {artifact.url && (
                <Button size="small" href={artifact.url} target="_blank" rel="noreferrer" sx={{ color: copperDark, fontWeight: 800 }}>
                  Open
                </Button>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <>
      <Tooltip title="Ask Casper">
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            right: 28,
            bottom: 28,
            zIndex: CASPER_OVERLAY_Z_INDEX,
            width: 64,
            height: 64,
            bgcolor: "#10100F",
            color: "#fff",
            border: "1px solid rgba(201,147,100,0.65)",
            boxShadow: "0 22px 46px rgba(16,16,15,0.26)",
            "&:hover": { bgcolor: "#000" },
          }}
        >
          <MiniGhost />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        hideBackdrop
        ModalProps={{ keepMounted: true }}
        sx={{ zIndex: CASPER_OVERLAY_Z_INDEX }}
        PaperProps={{
          sx: {
            zIndex: CASPER_OVERLAY_Z_INDEX,
            width: { xs: "100%", sm: 540 },
            maxWidth: { sm: "calc(100vw - 48px)" },
            height: { xs: "100%", sm: "min(760px, calc(100vh - 48px))" },
            top: { xs: 0, sm: 24 },
            right: { xs: 0, sm: 24 },
            bottom: { xs: 0, sm: 24 },
            p: 0,
            border: { xs: "none", sm: "1px solid rgba(184,115,51,0.18)" },
            borderRadius: { xs: 0, sm: 4 },
            background:
              "radial-gradient(circle at 18% 6%, rgba(209,161,122,0.16), transparent 30%), #fffdfb",
            boxShadow: "-22px 28px 90px rgba(16,16,15,0.22)",
            overflow: "hidden",
          },
        }}
      >
        <Stack sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.4}
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: "1px solid rgba(184,115,51,0.14)",
              backdropFilter: "blur(18px)",
              bgcolor: "rgba(255,253,251,0.86)",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                bgcolor: "#10100F",
                boxShadow: "0 14px 30px rgba(147,92,41,0.18)",
              }}
            >
              <MiniGhost />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6" fontWeight={900} sx={{ lineHeight: 1 }}>
                  Casper
                </Typography>
                <Chip
                  size="small"
                  label={(profile?.user?.role || user.role || "user").replace("-", " ")}
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    color: copperDark,
                    bgcolor: "rgba(184,115,51,0.1)",
                  }}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                Ask, troubleshoot, or run work without leaving this page.
              </Typography>
            </Box>
            <Tooltip title="Close">
              <IconButton onClick={() => setOpen(false)} sx={{ color: "text.secondary" }}>
              <CloseIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack
            spacing={1.75}
            sx={{
              px: 2.25,
              py: 2,
              overflow: "auto",
              flex: 1,
              minHeight: 0,
              overscrollBehavior: "contain",
            }}
          >
            {messages.length === 0 && !resolution && !error && (
              <Box
                sx={{
                  px: 2,
                  py: 2.2,
                  borderRadius: 3,
                  border: "1px solid rgba(184,115,51,0.18)",
                  bgcolor: "rgba(255,255,255,0.72)",
                  boxShadow: "0 18px 50px rgba(16,16,15,0.05)",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <MiniGhost />
                  <Box>
                    <Typography fontWeight={900}>What can I help with?</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ask about this page, troubleshoot a run, or start an action.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            {messages.length > 0 && (
              <Stack spacing={1.15}>
                {messages.map((message) => {
                  const isUserMessage = message.role === "user";
                  const isStatus = message.kind === "status";
                  const isPending = ["pending", "streaming"].includes(message.status) && !message.content;
                  if (isStatus) {
                    return (
                      <Stack key={message.id} direction="row" spacing={1} alignItems="center" sx={{ color: copperDark }}>
                        <MiniGhost />
                        <Typography variant="body2" fontWeight={800}>{message.content}</Typography>
                      </Stack>
                    );
                  }
                  return (
                    <Box key={message.id} sx={{ display: "flex", justifyContent: isUserMessage ? "flex-end" : "flex-start" }}>
                      <Box
                        sx={{
                          maxWidth: "86%",
                          width: isUserMessage ? "fit-content" : "100%",
                        }}
                      >
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1.15,
                            borderRadius: 2,
                            border: "1px solid rgba(184,115,51,0.32)",
                            bgcolor: isUserMessage ? "rgba(255,255,255,0.9)" : "rgba(255,252,249,0.94)",
                            boxShadow: "0 14px 36px rgba(16,16,15,0.04)",
                          }}
                        >
                          {isPending ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <MiniGhost />
                              <Typography variant="body2" color="text.secondary">Working on it...</Typography>
                            </Stack>
                          ) : (
                            <CasperMarkdown compact>{message.content}</CasperMarkdown>
                          )}
                          {!isUserMessage && renderArtifacts(message)}
                        </Box>
                        {isUserMessage ? renderPromptActions(message) : renderResponseActions(message)}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
            {error && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  color: "error.dark",
                  bgcolor: "rgba(211,47,47,0.07)",
                  border: "1px solid rgba(211,47,47,0.16)",
                }}
              >
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}
            {notice && (
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: notice.status === "auth_required" ? "warning.light" : "divider",
                  borderRadius: 2,
                  bgcolor: notice.status === "auth_required" ? "rgba(255, 193, 7, 0.08)" : "background.paper",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {notice.message || "Casper action prepared."}
                </Typography>
              </Box>
            )}
            {authRequired.length > 0 && (
              <Stack spacing={1}>
                {authRequired.map((signal) => (
                  <Button
                    key={`${signal.provider}-${signal.required_env || signal.tool_name || "oauth"}`}
                    variant="soft"
                    color="warning"
                    startIcon={<VpnKeyIcon />}
                    onClick={() => connectOAuth(signal)}
                  >
                    Connect {signal.provider_label || signal.provider}
                  </Button>
                ))}
              </Stack>
            )}
            {!resolution && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {suggestions.slice(0, 4).map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    onClick={() => {
                      setIntent(suggestion);
                    }}
                    variant="outlined"
                    sx={{
                      borderColor: "rgba(184,115,51,0.32)",
                      bgcolor: "rgba(255,255,255,0.72)",
                      fontWeight: 700,
                      "&:hover": {
                        bgcolor: "rgba(184,115,51,0.08)",
                        borderColor: "rgba(184,115,51,0.55)",
                      },
                    }}
                  />
                ))}
              </Stack>
            )}
            {agents.length > 0 && (
              <Stack spacing={1.25}>
                <Typography variant="subtitle2" fontWeight={900}>Best agents</Typography>
                {agents.map((agent) => (
                  <Box
                    key={`${agent.id}-${agent.version}`}
                    sx={{
                      p: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2.5,
                      bgcolor: "rgba(255,255,255,0.82)",
                      boxShadow: "0 16px 42px rgba(16,16,15,0.045)",
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Box sx={{ color: copperDark }}>
                        <PsychologyIcon />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography fontWeight={800} noWrap>{agent.display_name || agent.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          score {Math.round((agent.score || 0) * 100)}%
                        </Typography>
                      </Box>
                      <Button size="small" variant="soft" color="primary" onClick={() => runAgent(agent)}>
                        Run
                      </Button>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
            {sideEntities.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="subtitle2" fontWeight={900}>Also relevant</Typography>
                {sideEntities.slice(0, 6).map((entity) => (
                  <Stack
                    key={`${entity.type}-${entity.id}-${entity.version || ""}`}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ py: 0.75, borderBottom: "1px solid", borderColor: "divider" }}
                  >
                    <Typography noWrap sx={{ pr: 1 }}>{entity.display_name || entity.name}</Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Chip size="small" label={entity.type} variant="soft" color="primary" />
                      <Button size="small" variant="text" sx={{ color: copper }} onClick={() => executeEntity(entity)}>
                        Use
                      </Button>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            )}
          </Stack>
          <Box
            sx={{
              p: 2,
              borderTop: "1px solid rgba(184,115,51,0.14)",
              bgcolor: "rgba(255,253,251,0.92)",
              backdropFilter: "blur(18px)",
            }}
          >
            <Box
              sx={{
                border: "1px solid rgba(184,115,51,0.24)",
                bgcolor: "rgba(255,255,255,0.78)",
                borderRadius: 3,
                p: 1,
                boxShadow: "0 18px 46px rgba(16,16,15,0.07)",
              }}
            >
              <TextField
                value={intent}
                onChange={(event) => setIntent(event.target.value)}
                multiline
                minRows={3}
                maxRows={6}
                placeholder="Ask Casper..."
                disabled={busy}
                fullWidth
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    px: 1,
                    py: 0.75,
                    fontSize: 16,
                  },
                }}
              />
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.75 }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Tooltip title="Ask Casper">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!intent.trim() || busy}
                        onClick={handlePrimarySubmit}
                        sx={{
                          color: copper,
                          bgcolor: "rgba(184,115,51,0.08)",
                          "&:hover": { bgcolor: "rgba(184,115,51,0.14)" },
                        }}
                      >
                        <AutoAwesomeIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Session history">
                    <IconButton
                      size="small"
                      sx={messageActionButtonSx()}
                      onClick={() => {
                        setOpen(false);
                        navigate("/casper");
                      }}
                    >
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={artifactCount ? "View artifacts" : "No artifacts yet"}>
                    <span>
                      <IconButton
                        size="small"
                        sx={messageActionButtonSx()}
                        onClick={() => {
                          setOpen(false);
                          navigate("/casper");
                        }}
                        disabled={!artifactCount}
                      >
                        <Badge badgeContent={artifactCount} color="primary" max={99} invisible={!artifactCount} overlap="circular">
                          <Inventory2OutlinedIcon />
                        </Badge>
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Tooltip title={agents.length === 1 ? "Run selected match" : "Ask Casper"}>
                  <span>
                    <IconButton
                      disabled={!intent.trim() || busy}
                      onClick={handlePrimarySubmit}
                      sx={{
                        ...commandIconSx,
                        color: "#fff",
                        bgcolor: copper,
                        "&:hover": {
                          bgcolor: copperDark,
                          transform: "translateY(-1px)",
                          boxShadow: "0 16px 32px rgba(184,115,51,0.24)",
                        },
                        "&.Mui-disabled": {
                          color: "rgba(0,0,0,0.18)",
                          bgcolor: "rgba(184,115,51,0.08)",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {busy ? <MiniGhost /> : <SendIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Drawer>
    </>
  );
}
